import { FormControlLabel, Checkbox, FormGroup, Link, Radio, SxProps, Theme } from "@mui/material";
import {
  Children,
  forwardRef,
  isValidElement,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { DEFAULT_DEBOUNCE_TIME } from "../../../constants/elements";
import { sr25Blue } from "../../../theming/sr25Colors";
import { OverflowText } from "../../DataDisplay/OverflowText/OverflowText";
import { SearchField } from "../SearchField/SearchField";

export type SrFilterProps = {
  name: string;
  children: ReactNode;
  sx?: SxProps<Theme>;
  optionsSearch?: boolean;
  showAll?: boolean;
  onOptionsSearch?: (keyword: string) => void | Promise<void>;
  optionsSearchKey?: string;
  optionsSearchLoading?: boolean;
  searchPlaceholder?: string;
};

const normalizeKeyword = (keyword: string): string => keyword.toLowerCase();

export const getOptionValue = (option: ReactNode): string => {
  if (!isValidElement<{ value?: unknown }>(option)) {
    return "";
  }

  return typeof option.props.value === "string" ? option.props.value : "";
};

const getOptionKey = (option: ReactNode, fallback: number): string | number => {
  if (isValidElement(option) && option.key != null) {
    return option.key;
  }

  return fallback;
};

const scoreOptionValue = (value: string, keyword: string): number => {
  const normalizedValue = value.toLowerCase();
  if (normalizedValue.startsWith(keyword)) {
    return 0;
  }
  if (normalizedValue.includes(keyword)) {
    return 1;
  }
  return 2;
};

export const sortByKeyword = <T,>(
  items: T[],
  keyword: string,
  getText: (item: T) => string,
): T[] => {
  if (!keyword.length) {
    return items;
  }

  const normalizedKeyword = normalizeKeyword(keyword);
  return [...items].sort((a, b) => {
    const aValue = getText(a);
    const bValue = getText(b);
    const aScore = scoreOptionValue(aValue, normalizedKeyword);
    const bScore = scoreOptionValue(bValue, normalizedKeyword);

    if (aScore !== bScore) {
      return aScore - bScore;
    }

    return aValue.localeCompare(bValue);
  });
};

const optionMatchesKeyword = (option: ReactNode, keyword: string): boolean => {
  if (!keyword.length) {
    return true;
  }

  const value = getOptionValue(option);
  return !!value && value.toLowerCase().includes(keyword);
};

export const SrFilter = ({
  name,
  children,
  sx,
  optionsSearch,
  showAll,
  onOptionsSearch,
  optionsSearchKey,
  optionsSearchLoading,
  searchPlaceholder = "Search",
  ...props
}: SrFilterProps): ReactElement => {
  // Number to limit the displayed Options
  const OPTIONS_SEARCH = 12;
  const OPTIONS_SHOW_ALL = 7;

  const [keyword, setKeyword] = useState<string>("");
  const [showAllOn, setShowAllOn] = useState<boolean>(!!showAll);

  // Keyword ref so we can use it in the below effect without re-running it on every keystroke
  const keywordRef = useRef(keyword);
  useEffect(() => {
    keywordRef.current = keyword;
  }, [keyword]);

  // The identity-driven re-fire: consumers re-mint onOptionsSearch when
  // the context an active search was computed under changes (see the
  // search-filters Filter's per-mode callbacks), so a fresh identity —
  // or a changed optionsSearchKey, the explicit form of the same signal —
  // re-runs the still-typed keyword against the new context. keywordRef
  // keeps keystrokes from re-running this effect; the debounced change
  // path owns those.
  useEffect(() => {
    if (!onOptionsSearch) {
      return;
    }
    const currentKeyword = keywordRef.current;
    if (currentKeyword) {
      onOptionsSearch(currentKeyword);
    }
  }, [onOptionsSearch, optionsSearchKey]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setKeyword(value);
      onOptionsSearch?.(value);
    },
    [onOptionsSearch],
  );

  const showSearchField = (): boolean => {
    return optionsSearch || !!onOptionsSearch || Children.count(children) > OPTIONS_SEARCH;
  };

  const showShowAllButton = (): boolean => {
    const optionsAmount = Children.count(children);
    // When using remote search, keyword doesn't affect local filtering, so ignore it
    const shouldCheckKeyword = !onOptionsSearch;
    return (
      !showAllOn && (!shouldCheckKeyword || !keyword.length) && optionsAmount > OPTIONS_SHOW_ALL
    );
  };

  /*
   * Filter or limit the displayed Options based on the defined limits,
   * but keep all options mounted in the DOM so form state is preserved.
   */
  const renderChildren = (): ReactNode => {
    const options = Children.toArray(children);
    const shouldLimitOptions = showShowAllButton();
    const shouldFilterOptions = !onOptionsSearch && showSearchField() && !shouldLimitOptions;
    const sortedOptions = shouldFilterOptions
      ? sortByKeyword(options, keyword, getOptionValue)
      : options;

    return sortedOptions.map((child, index) => {
      const matchesKeyword =
        !shouldFilterOptions || optionMatchesKeyword(child, normalizeKeyword(keyword));
      const isVisible = (!shouldLimitOptions || index < OPTIONS_SHOW_ALL) && matchesKeyword;
      const key = getOptionKey(child, index);

      return (
        <div key={key} hidden={!isVisible}>
          {child}
        </div>
      );
    });
  };

  return (
    <FormGroup {...props} sx={[...(Array.isArray(sx) ? sx : [sx])]}>
      {/* Show the Options Search Input if more than {OPTIONS_SEARCH} options OR if the optionsSearch is TRUE, OR if onOptionsSearch is provided */}
      {showSearchField() ? (
        <SearchField
          id={name}
          placeholder={searchPlaceholder}
          onChange={handleSearchChange}
          loading={optionsSearchLoading}
          debounceTime={DEFAULT_DEBOUNCE_TIME}
          sx={{ mb: 1.5, ml: -1.5 }}
        />
      ) : null}
      {renderChildren()}
      {showShowAllButton() ? (
        <Link
          component="button"
          variant="body1"
          sx={(theme) => ({
            mt: 0.75,
            ml: -0.75,
            textAlign: "left",
            color: sr25Blue[600],
            ...theme.applyStyles("dark", {}),
            [theme.getColorSchemeSelector("dark")]: {
              color: sr25Blue[500],
            },
          })}
          onClick={() => {
            setShowAllOn(true);
          }}
        >
          Show all
        </Link>
      ) : null}
    </FormGroup>
  );
};

export type SrFilterCheckboxProps = {
  id?: string;
  name?: string;
  label?: ReactNode;
  value: string;
  defaultChecked?: boolean;
  checked?: boolean;
  onCheckedChange?(isChecked: boolean): void;
};

const FilterCheckbox = forwardRef<HTMLInputElement, SrFilterCheckboxProps>(
  ({ id, name, label, value, defaultChecked, checked, onCheckedChange }, ref) => {
    return (
      <FormControlLabel
        disableTypography
        label={
          <OverflowText variant="inputLabel" sx={{ color: "fg.600" }}>
            {label ?? value}
          </OverflowText>
        }
        control={
          <Checkbox
            inputRef={ref}
            id={id}
            name={name}
            value={value}
            defaultChecked={defaultChecked}
            checked={checked}
            onChange={(e) => onCheckedChange?.(e.target.checked)}
          />
        }
      />
    );
  },
);

export type SrFilterRadioProps = {
  id?: string;
  name?: string;
  label?: ReactNode;
  value: string;
  checked?: boolean;
  onChange?(value: string): void;
};

const FilterRadio = forwardRef<HTMLInputElement, SrFilterRadioProps>(
  ({ id, name, label, value, checked, onChange }, ref) => {
    return (
      <FormControlLabel
        disableTypography
        label={
          <OverflowText variant="inputLabel" sx={{ color: "fg.600" }}>
            {label ?? value}
          </OverflowText>
        }
        control={
          <Radio
            inputRef={ref}
            id={id}
            name={name}
            value={value}
            checked={checked}
            onChange={(e) => {
              if (e.target.checked) {
                onChange?.(value);
              }
            }}
          />
        }
      />
    );
  },
);

SrFilter.Checkbox = FilterCheckbox;
SrFilter.Radio = FilterRadio;
